'use client';

import React from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardSubtitle,
  CardTitle,
  CardAction,
  CardFooter,
} from '@packages/shared/src/ui/Card/Card';
import { useI18n } from '@packages/shared/src/hooks/useI18n/useI18n';
import { useAuthQuery } from '@packages/shared/src/hooks/useAuthQuery/useAuthQuery';
import { CustomerDocument } from '@packages/gql/generated/shopping/CustomerDocument';
import { FormattedMessage } from 'react-intl';
import { useLogout } from '@packages/modules/src/Login/api/useLogout';
import { Button } from '@packages/shared/src/ui/Button/Button';
import { Link } from '@packages/modules/src/Link/Link';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { getLocalizedPath } from '@packages/shared/src/utils/getLocalizedPath/getLocalizedPath';
import type { CustomerStatusData } from './types';
import { Avatar, AvatarIcon } from '../Avatar/Avatar.client';
import { JoeConnectedSection } from '../Joe/JoeConnectedSection.client';

/* GraphQL */ `
  query Customer($locale: String!) {
    customer(locale: $locale) {
      __typename
      ... on CustomerDataWithStatus {
        firstName
        lastName
        email
        businessPartnerNo
        # Read by the jö gate in useJoeConnection, which runs this same document so both share one
        # request. Keep it selected here even though the card itself does not render it.
        loyaltyIntegrationAllowed
      }
    }
  }
`;

export type CustomerCardProps = {
  advantages: CustomerStatusData['advantages'];
  /**
   * Renders the connected jö Bonus Club section before the personal-data footer.
   * Supplied exclusively by the authoritative `CustomerOverview` placement branch.
   */
  hasConnectedJoe?: boolean;
};

export const CustomerCard = ({ advantages, hasConnectedJoe = false }: CustomerCardProps) => {
  const { locale } = useI18n();
  const { logout } = useLogout();
  const {
    order: {
      paths: { myAccountPersonalData },
    },
  } = useConfig();
  const [{ data }] = useAuthQuery({
    query: CustomerDocument,
    variables: { locale },
  });

  if (data?.customer.__typename === 'CustomerDataWithStatus') {
    const { customer } = data;
    return (
      <Card hasAutoSeparation>
        <CardHeader>
          <Avatar>
            <AvatarIcon className="icon-emp-user" />
          </Avatar>
          <div>
            <CardTitle>
              {customer.firstName} {customer.lastName}
            </CardTitle>
            <CardSubtitle>{customer.email}</CardSubtitle>
            <CardDescription>
              <FormattedMessage
                id="CustomerCard.LogoutText"
                defaultMessage="Du bist nicht {name}? <logoutLink>Jetzt abmelden</logoutLink>"
                values={{
                  name: customer.firstName,
                  logoutLink: (chunks: React.ReactNode) => (
                    <Button variant="link" onClick={logout} className="min-h-auto">
                      {chunks}
                    </Button>
                  ),
                }}
              />
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent className="text-size-body-sm pb-2">
          <div className="pb-1">
            <FormattedMessage
              id="CustomerCard.BusinessPartnerNumber"
              defaultMessage="Kundennummer: <strong>{businessPartnerNo}</strong>"
              values={{
                businessPartnerNo: customer.businessPartnerNo,
                strong: (chunks: React.ReactNode) => <strong>{chunks}</strong>,
              }}
            />
          </div>
          {advantages?.list &&
            advantages.list.map((entry) => (
              <div className="flex items-center gap-1" key={entry}>
                <span className="icon-emp-checkmark text-color-success size-3" />
                <span>{entry}</span>
              </div>
            ))}
        </CardContent>
        {advantages?.links && advantages.links.length > 0 && (
          <CardContent className="flex flex-col gap-1 pb-2">
            {advantages.links.map((entry) => (
              <CardAction
                icon={
                  entry.iconType === 'premium' ? 'icon-emp-premiumlounge' : 'icon-emp-flexipay-logo'
                }
                key={entry.href}
              >
                <Link href={entry.href}>{entry.description}</Link>
              </CardAction>
            ))}
          </CardContent>
        )}
        {hasConnectedJoe && <JoeConnectedSection />}
        <CardFooter className="flex items-center gap-2 px-2 py-1">
          <CardAction icon="icon-emp-user">
            <Link href={getLocalizedPath(myAccountPersonalData, locale)}>
              <FormattedMessage
                id="CustomerCard.PersonalDataLink"
                defaultMessage="Meine persönlichen Daten"
              />
            </Link>
          </CardAction>
        </CardFooter>
      </Card>
    );
  }

  // For the moment do not render anything if there is an error or no data
  // This case should not happen but what if?
  return null;
};
